import * as React from 'react'

import {
  GlobalTwoBestSellers,
  ProductImage,
  ProductVariant,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  FeaturedProducts,
  FeaturedProductsProps,
} from '@thg-commerce/enterprise-widget-featured-products'

import { BaseWidgetProps } from '../types'
import { ProductProps } from '@thg-commerce/enterprise-widget-featured-products/src/FeaturedProducts'
import { styled } from '@thg-commerce/enterprise-theme'
import { ProductBlock } from '@thg-commerce/enterprise-aurora-landing/aurora-schema'
import { i18n } from '@thg-commerce/enterprise-core'

export interface ProductVariantItem
  extends Omit<
    ProductVariant,
    'product' | 'choices' | 'content' | 'inStock' | 'availabilityMessage'
  > {
  product: {
    reviews: {
      averageScore: number
      total: number
      __typename: 'ProductReviews'
    }
    url: string
    __typename: 'Product'
    images: ProductImage[]
  }
}
export interface TwoBestSellersWithProductVariants
  extends Omit<GlobalTwoBestSellers, 'ProductOne' | 'ProductTwo'> {
  ProductVariantOne?: ProductProps
  ProductVariantTwo?: ProductProps
}

export type FeaturedProductsRendererProps = BaseWidgetProps &
  Omit<TwoBestSellersWithProductVariants, 'query'> & {
    productBlockOne: ProductBlock
    productBlockTwo: ProductBlock
  }

const StyledFeaturedProducts = styled(FeaturedProducts)`
  margin: 0 auto;
  max-width: ${(props) => props.theme.site.siteWidth};
`

export const FeaturedProductsRenderer = (
  props: FeaturedProductsRendererProps,
) => {
  const i18nText = {
    closeI18nText: {
      closeAriaLabel: i18n('general.modal.close.button.arialabel'),
      closeLabel: i18n('general.modal.close.button.label'),
    },
  }
  const items: ProductProps[] = []

  if (props.ProductVariantOne && props.ProductVariantTwo) {
    items.push({
      ...props.ProductVariantOne,
      attributes: {
        justifyContent: 'start',
        alignItems: 'center',
      },
    })
    items.push({
      ...props.ProductVariantTwo,
      attributes: {
        justifyContent: 'start',
        alignItems: 'center',
      },
    })
  }

  if (props.productBlockOne && props.productBlockTwo) {
    items.push({
      ...props.productBlockOne,
      title: {
        value: props.productBlockOne.title,
      },
      review: {
        starRating: props.productBlockOne?.reviews?.averageScore,
        numberOfReviews: props.productBlockOne?.reviews?.total,
      },
      marketedSpecialOffer: {
        ...props.productBlockOne?.marketedSpecialOffer,
        i18nText,
        onlyDisplayOfferBadge: true,
        shouldRemoveScrollLock: false,
        title: props.productBlockOne?.marketedSpecialOffer?.titleText,
        landingPageLink:
          props.productBlockOne?.marketedSpecialOffer?.landingPageLink,
      },
      attributes: {
        justifyContent: 'start',
        alignItems: 'center',
      },
    })
    items.push({
      ...props.productBlockTwo,
      i18nText,
      title: {
        value: props.productBlockTwo.title,
      },
      review: {
        starRating: props.productBlockTwo?.reviews?.averageScore,
        numberOfReviews: props.productBlockTwo?.reviews?.total,
      },
      marketedSpecialOffer: {
        ...props.productBlockTwo?.marketedSpecialOffer,
        onlyDisplayOfferBadge: true,
        shouldRemoveScrollLock: false,
        title: props.productBlockTwo?.marketedSpecialOffer?.titleText,
        landingPageLink:
          props.productBlockTwo?.marketedSpecialOffer?.landingPageLink,
      },
      attributes: {
        justifyContent: 'start',
        alignItems: 'center',
      },
    })
  }

  if (!items.length) {
    return null
  }

  const featuredProductProps: FeaturedProductsProps = {
    productBlocks: {
      items,
    },
  }
  return <StyledFeaturedProducts {...featuredProductProps} />
}
